// ============================================================================
// useCanManageSubs — authoritative sub-management gate for the current user
// on a specific facility. 2026-06-15.
//
// Mirrors the DB SECURITY DEFINER function public.can_manage_subs(p_facility_id):
//   has_full_am_authority() OR (is_am_role() AND is_my_facility(facility))
// i.e. "the assigned account_manager can manage subs on their OWN facilities,"
// not just full-authority roles. We call the RPC rather than re-deriving the
// rule client-side so the UI gate can never drift from the DB enforcement
// (RLS srr_update + enforce_approval_scope + the cutover RPC all key off it).
//
// Returns { canManage, loading }. Defaults to false until resolved so we never
// flash a control the RPC would reject.
// ============================================================================

import { useEffect, useState } from 'react';
import { supabase } from './supabase';

export function useCanManageSubs(facilityId: string | null | undefined): {
  canManage: boolean;
  loading: boolean;
} {
  const [canManage, setCanManage] = useState(false);
  const [loading, setLoading] = useState(Boolean(facilityId));

  useEffect(() => {
    if (!facilityId) {
      setCanManage(false);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const { data, error } = await supabase.rpc('can_manage_subs', {
          p_facility_id: facilityId,
        });
        if (cancelled) return;
        // On any error, fail closed (no control rendered). The RPC is the real
        // enforcement; a hidden control just avoids a guaranteed-reject click.
        setCanManage(error ? false : Boolean(data));
      } catch {
        if (!cancelled) setCanManage(false);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [facilityId]);

  return { canManage, loading };
}
